import { useCallback, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { useFormikContext } from 'formik';
import type { GenericList } from '@sda/helpers/helpers.types';

type MemoFunction = () => unknown;
type MemoKey = [GenericList<unknown>, string?];

const useDeepMemo = (memoFunction: MemoFunction, key: MemoKey) => {
  const ref = useRef<{ key: MemoKey, value: unknown }>();

  if (!ref.current || !isEqual(key, ref.current.key)) {
    ref.current = { key, value: memoFunction() };
  }

  return ref.current.value;
};

/**
 * Defines a callback that does not change after mount
 * @param  {Function} callback
 * @return {Function}
 */
const useStaticCallback = <T extends (...args: any) => any>(callback: T): T => useCallback(callback, []); // eslint-disable-line

/**
 * Retrieves the full Formik context
 * @return {FormikContext}
 */
export const useFormik = <T = GenericList<unknown>>() => useFormikContext<T>();

/**
 * Retrieves the submitForm callback from the formik context
 * @return {Function}
 */
export const useFormSubmit = () => {
  const { submitForm } = useFormik();

  return useStaticCallback(submitForm);
};

/**
 * Retrieves the setFieldValue callback from the formik context
 * @return {Function}
 */
export const useSetFieldValue = () => {
  const { setFieldValue } = useFormik();

  return useStaticCallback(setFieldValue);
};

/**
 * Retrieves the setValues callback from the formik context
 * @return {Function}
 */
export const useSetValues = () => {
  const { setValues } = useFormik();

  return useStaticCallback(setValues);
};

/**
 * Retrieves the setSubmitting callback from the formik context
 * @return {Function}
 */
export const useSetSubmitting = () => {
  const { setSubmitting } = useFormik();

  return useStaticCallback(setSubmitting);
};

/**
 * Retrieves the resetForm callback from the formik context
 * @return {Function}
 */
export const useResetForm = () => {
  const { resetForm } = useFormik();

  return useStaticCallback(resetForm);
};

export const useSetStatus = () => {
  const { setStatus } = useFormik();

  return useStaticCallback(setStatus);
};

/**
 * Retrieves one or more values from the original Formik store
 * @param  {string} property?
 * @return {any}
 */
export const useFormInitialValues = <T = unknown>(property?: string): T => {
  const { initialValues: values } = useFormik();

  return useDeepMemo(() => {
    if (property) {
      const buildValuesReturn = (valueObject, propArray) => {
        let newValueObject = valueObject;
        const nextProperty = propArray.shift();

        if (nextProperty && Object.prototype.hasOwnProperty.call(newValueObject, nextProperty)) {
          newValueObject = buildValuesReturn(newValueObject[nextProperty], propArray);
        }

        return newValueObject;
      };

      if (property.indexOf('.') > -1) {
        return buildValuesReturn(values, property.split('.'));
      }

      return values[property];
    }

    return values;
  }, [values, property]) as T;
};

/**
 * Retrieves one or more values from the Formik store
 * @param  {string} property?
 * @return {any}
 */
export const useFormValues = <T = unknown>(property?: string): T => {
  const { values } = useFormik() || {};

  return useDeepMemo(() => {
    if (property) {
      if (!values) {
        return null;
      }

      const buildValuesReturn = (valueObject, propArray) => {
        let newValueObject = valueObject;
        const nextProperty = propArray.shift();

        if (nextProperty && Object.prototype.hasOwnProperty.call(newValueObject, nextProperty)) {
          newValueObject = buildValuesReturn(newValueObject[nextProperty], propArray);
        }

        return newValueObject;
      };

      if (property.indexOf('.') > -1) {
        return buildValuesReturn(values, property.split('.'));
      }

      return values[property];
    }

    return values;
  }, [values, property]) as T;
};

/**
 * Retrieves one or more errors from the Formik store
 * @param  {string} property?
 * @return {any}
 */
export const useFormErrors = (property) => {
  const { errors } = useFormik();

  return useMemo(() => {
    if (property) {
      return errors[property];
    }

    return errors;
  }, [property, errors]);
};

/**
 * Fetches the current dirty state from the Formik store
 * @return {boolean}
 */
export const useFormDirtyFlag = () => {
  const { dirty, isSubmitting, isValidating } = useFormik();

  return useMemo(() => dirty && !isSubmitting && !isValidating, [dirty, isSubmitting, isValidating]);
};
